import {
  format,
  isBefore,
  isFuture,
  isValid,
  parseISO,
  subYears,
  isLeapYear,
} from "date-fns";
import enUS from "date-fns/locale/en-US";
import es from "date-fns/locale/es";
import { MCTDate } from "../@types";
import { inputIsBetween, containsLetters } from "./objectUtilities";

export const dateFromMCTDate = (date: MCTDate): Date => {
  return new Date(
    parseInt(date.year, 10),
    parseInt(date.month, 10) - 1,
    parseInt(date.day, 10)
  );
};

export const isEmptyDate = (value: MCTDate): boolean => {
  return !value.day && !value.month && !value.year;
};

export const isNotValid = (
  value: string,
  low: number,
  high: number,
  strict?: boolean
): boolean => {
  if (!value && strict) {
    return true;
  } else if (!value) {
    return false;
  }
  if (containsLetters(value)) return true;
  if (!inputIsBetween(Number(value), low, high)) return true;
  return false;
};

const currentYear = new Date().getFullYear();

export const isInvalidDatePart = {
  day: (value: string, strict = false): boolean =>
    isNotValid(value, 1, 31, strict),
  month: (value: string, strict = false): boolean =>
    isNotValid(value, 1, 12, strict),
  year: (value: string, strict = false): boolean => {
    if (strict && value.length < 4) return true;
    else if (value.length < 4) return false;
    return isNotValid(value, currentYear - 120, currentYear + 1, strict);
  },
};

export const isValidDate = (mctDate: MCTDate): boolean => {
  const strict = true;
  const dayIsValid = !isInvalidDatePart.day(mctDate.day, strict);
  const monthIsValid = !isInvalidDatePart.month(mctDate.month, strict);
  const yearIsValid = !isInvalidDatePart.year(mctDate.year, strict);

  const thirtyOneDayMonths = ["1", "3", "5", "7", "8", "10", "12"];
  const isRealDay =
    Number(mctDate.day) < 31 ||
    thirtyOneDayMonths.includes(mctDate.month.replace(/^0/, ""));
  const februaryIssue =
    ["2", "02"].includes(mctDate.month) &&
    (Number(mctDate.day) > 29 ||
      (!isLeapYear(new Date(parseInt(mctDate.year, 10), 7, 7)) &&
        Number(mctDate.day) === 29));

  return (
    dayIsValid && monthIsValid && yearIsValid && isRealDay && !februaryIssue
  );
};

export const checkBirthDateValidity = (dob: MCTDate): boolean => {
  let ValidDate = true;

  if (!isValidDate(dob)) ValidDate = false;

  const dobDate = dateFromMCTDate(dob);
  // Does it quack like a duck?
  if (!isValid(dobDate)) ValidDate = false;

  // Does it quack like a cyborg duck?
  if (isFuture(dobDate)) ValidDate = false;

  // Does it quack like a mummified duck?
  if (isBefore(dobDate, subYears(new Date(), 130))) ValidDate = false;

  return ValidDate;
};

export const dateFormatter = ({ month, day, year }: MCTDate): MCTDate => {
  return {
    month: month.replace(/\D+/g, "").substring(0, 2),
    day: day.replace(/\D+/g, "").substring(0, 2),
    year: year.replace(/\D+/g, "").substring(0, 4),
  };
};

export const formatDate = (
  date: string | number | Date,
  fmt: string
): string => {
  const localeString = window.navigator.language.substring(0, 2);
  if (typeof date === "string") {
    date = parseISO(date);
  }
  const locale = localeString == "en" ? enUS : es;

  return format(date, fmt, { locale });
};

export const formatMCTDate = (date: MCTDate | undefined): string => {
  if (!date || !date.day || !date.month || !date.year) {
    return "N/A";
  }

  return `${date.month}/${date.day}/${date.year}`;
};

export const MCTDateFromString = (date: string): MCTDate => {
  if (date.match(/\d{4}-\d{2}-\d{2}/)) {
    return {
      month: date.substring(5, 7),
      day: date.substring(8),
      year: date.substring(0, 4),
    };
  }
  return { day: "", month: "", year: "" };
};
